import axios, { AxiosError } from 'axios'
import isNil from 'lodash/isNil'
import { NextPage } from 'next'
import Head from 'next/head'
import styled from 'styled-components'

import Bugsnag from '@bugsnag/js'
import { GetCMSArticle, Section, useLazyGetCMSArticleQuery } from 'api/cmsApi'
import { useFetchUserSegmentsQuery } from 'api/consumerSegmentAccessApi'
import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useLogExperiment, useStartExperiment } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import Hero from 'homepage/Hero'
import { NavigationSection } from 'homepage/NavigationSection'
import PromotedProductSet from 'homepage/PromotedProductSet'
import SplitScreen from 'homepage/SplitScreen'
import StoresAndHistory from 'homepage/StoresAndHistory'
import { serverSideConfigs, t } from 'localization'
import { useEffect, useState } from 'react'
import { serverAssign } from 'server/services/serverExperimentService'
import { logger } from 'server/utils/logger'
import { MOSAIC_BASE_URL } from 'server/utils/mosaicBaseUrl'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import CMSSectionType from 'shared/enums/CMSSectionType'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

const TP = 'pages.index'

type HomePageProps = {
  error?: unknown
  homeFeedData: GetCMSArticle & {
    /** Indicates the group assigned in the experiment when SSR was performed.
     * Needed if the home feed needs to be refetched client-side
     * (i.e. from CF cache with incorrect darwin group).
     * TODO: Remove after experiment is finished.
     */
    darwinGroup: 'control' | 'variant'
  }
}

// CMS article slugs used for the home feed experiment
const CONTROL_ARTICLE = 'fc-home-feed'
const VARIANT_ARTICLE = 'fc-home-feed-test-variant-1'

const HomePage: NextPage<HomePageProps> = ({ error, homeFeedData }) => {
  // CLIENT EXPERIMENT web_fc_home_feed_variant INIT
  const areExperimentsEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const isHomeFeedVariantEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_HOME_FEED_VARIANT)
  const enableHomeFeedVariantExperiment = areExperimentsEnabled && isHomeFeedVariantEnabled
  const homeFeedExperiment: EXPERIMENT_NAME = 'web_fc_home_feed_variant'

  const [enableLogExperiment, setEnableLogExperiment] = useState(false) // Controls logging once correct feed is displayed
  const [homeFeed, setHomeFeed] = useState<GetCMSArticle | null>(homeFeedData) // Initial home feed data from SSR or cache
  const [getCMSArticle] = useLazyGetCMSArticleQuery() // Lazy query to fetch CMS feed data
  const { experimentState, isAssigning: isExperimentLoading } = useStartExperiment(
    homeFeedExperiment,
    !enableHomeFeedVariantExperiment,
  )

  // Check for experiment state once assigned to determine if refetch is needed
  useEffect(() => {
    if (experimentState?.groupAssigned && !isExperimentLoading) {
      if (experimentState.groupAssigned !== homeFeedData?.darwinGroup) {
        // Should refetch feed
        const article =
          experimentState?.groupAssigned === 'variant' ? VARIANT_ARTICLE : CONTROL_ARTICLE
        getCMSArticle(article)
          .then((queryResponse) => {
            const { data } = queryResponse
            if (!data) {
              // es-lint-disable-next-line no-console
              console.warn(`No data returned for CMS article:`, article)
              return
            }
            setHomeFeed(data) // Update home feed with new data
            setEnableLogExperiment(true) // Ok to log experiment exposure now
          })
          .catch((error) => {
            // es-lint-disable-next-line no-console
            console.error(`Error fetching CMS Article Data:`, error)
          })
      } else {
        // No need to refetch, just enable logging
        setEnableLogExperiment(true) // Ok to log experiment exposure now
      }
    }
  }, [isExperimentLoading, experimentState?.groupAssigned])

  useLogExperiment(homeFeedExperiment, !enableHomeFeedVariantExperiment || !enableLogExperiment)
  // CLIENT EXPERIMENT web_fc_home_feed_variant END

  const { country: countryCode } = useShoppingRegionContext()
  const { data: userSegmentData } = useFetchUserSegmentsQuery({
    countryCode,
  })
  const segmentIds = userSegmentData?.userSegmentsList?.map((segment) => segment.id)
  const authorizedFeedSections =
    homeFeed?.sections?.filter((section: any) => {
      return !section?.segmentId || segmentIds?.includes(section?.segmentId)
    }) || []

  /**
   * Returns the component for each section type in the feed.
   * If the section type is not supported, it will log a warning and return null.
   * @param sections
   */
  const getSectionComponent = (sections?: Section[] | undefined) => {
    if (!!sections?.length) {
      return sections.map((section: Section, i: number) => {
        const { id, type, uuid, ...sectionProps } = section
        switch (type) {
          case CMSSectionType.Hero:
          case CMSSectionType.VideoHero:
            return (
              <Hero
                key={`${uuid || id}`}
                draftId={homeFeed?.uuid || homeFeed?.id}
                index={i}
                sectionId={uuid || id}
                sectionType={type}
                {...sectionProps}
              />
            )
          case CMSSectionType.NavigationSection:
            return (
              <NavigationSection
                key={`${uuid || id}`}
                last={i + 1 === sections.length}
                {...sectionProps}
              />
            )
          case CMSSectionType.ProductTemplateCarousel:
            return (
              <PromotedProductSet
                draftId={`${homeFeed?.uuid || homeFeed?.id || ''}`}
                index={i + 1}
                key={uuid || id}
                sectionId={`${uuid || id}`}
                {...sectionProps}
              />
            )
          case CMSSectionType.TextAndImage:
            return <SplitScreen key={uuid || id} {...sectionProps} />
          case CMSSectionType.CmsSectionType:
            // es-lint-disable-next-line no-console
            console.info(`CMS Section type ${type} is not supported (section id=${uuid || id}).`)
            Bugsnag.notify(
              new Error(`CMS Section type ${type} is not supported (section id=${uuid || id}).`),
              (event) => {
                event.addMetadata('CMSsection', section)
                event.context = 'HomePage'
              },
            )
            return null
        }
      })
    }
  }

  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Sneakers. Here. | Flight Club')}</title>
      </Head>
      <Sections>
        {!error && getSectionComponent(authorizedFeedSections)}
        <StoresAndHistory data-testid="storesAndHistory" />
      </Sections>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale } = context
  const { featureFlags } = req
  try {
    // SSR EXPERIMENT web_fc_home_feed_variant INIT
    const areExperimentsEnabled = featureFlags?.[FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS] ?? false
    const isHomeFeedVariantEnabled = !!featureFlags?.[FeatureFlag.TEMP_WEB_FC_HOME_FEED_VARIANT]
    const homeArticle = await (async () => {
      if (areExperimentsEnabled && isHomeFeedVariantEnabled) {
        const experimentName: EXPERIMENT_NAME = 'web_fc_home_feed_variant'
        try {
          const assignResponse = await serverAssign({ experimentName, req, res })
          if (assignResponse?.group?.name && assignResponse.group.name === 'variant') {
            return VARIANT_ARTICLE
          }
        } catch (error) {
          return CONTROL_ARTICLE
        }
      }

      return CONTROL_ARTICLE
    })()

    const feedUrl = `${MOSAIC_BASE_URL}/api/v1/mosaic/articles/${homeArticle}?locale=${locale}`
    const { data } = await axios.get<GetCMSArticle>(feedUrl)
    const homeFeedData = {
      ...data,
      darwinGroup: homeArticle === VARIANT_ARTICLE ? 'variant' : 'control',
    }
    // SSR EXPERIMENT web_fc_home_feed_variant END

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        homeFeedData,
      },
    }
  } catch (error) {
    const err = error as Error | AxiosError
    logger.error(error, 'Error rendering home page feed')

    if (!isNil(res)) {
      res.statusCode = 500
    }

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        error: { status: 500, message: err.message },
      },
    }
  }
}

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 3300px;
`

export default HomePage
